import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  ReconDataResp, ReconsRequestParams,
  ReconSyncStatus, ReconUnmatchedRow,
  BankMandiriReportRow
} from '../../interfaces/recons.interface';
import {FlatpickrOptions} from "ng2-flatpickr";
import {firstValueFrom, map, Observable, Subject, takeUntil} from "rxjs";
import {UserData} from "../../interfaces/users.interface";
import {ProvidersService} from "../../services/providers/providers.service";
import {Store} from "@ngrx/store";
import {selectCurrentUser} from "../../services/users/user.selectors";
import { getOffset, showNotification } from '../../shared/helpers/helpers';
import {OptionItem} from "../../shared/types/api-responses.type";
import {ReconsService} from "../../services/recons/recons.service";
import * as XLSX from 'xlsx';


@Component({
  selector: 'app-recon',
  templateUrl: './recon.component.html',
  styleUrls: ['./recon.component.css']
})
export class ReconComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  loading = false;
  syncing = false;             // true while the Tarik Data button is in flight
  matching = false;            // true while the Match button is in flight
  downloadingBM = false;       // true while a Bank Mandiri report is being assembled
  activeTab: 'recon' | 'unmatched' = 'recon';
  unmatchedRows: ReconUnmatchedRow[] = [];
  unmatchedTotal = 0;
  unmatchedLoading = false;
  private pollTimer: any = null;
  providers: OptionItem[] = [];

  @ViewChild('dateFromPicker') dateFromPicker: any;
  dateFromConfig: FlatpickrOptions = {
    altInput: true,
    altFormat: 'd-m-Y',
    dateFormat: 'Y-m-d',
  };

  params = new ReconsRequestParams();
  totalRows = 0;
  pageSize = 10;

  cols = [
    { field: 'no', title: 'No' },
    { field: 'ref_id', title: 'Reference ID' },
    { field: 'bill_id', title: 'Bill ID' },
    { field: 'period', title: 'Period' },
    { field: 'amount', title: 'Amount' },
    { field: 'trx_date', title: 'Trx Date' },
    { field: 'status', title: 'Status' },
  ];

  pageSizeOptions = [5, 10, 25, 50, 100];
  rows: ReconDataResp[] = [];
  rowsOrigin: ReconDataResp[] = [];

  // Options for the Status filter dropdown. Values mirror recons.status
  // in the DB: '0' = matched/Success, '1' = pending. The empty/null
  // value (cleared dropdown) means "no filter".
  statusOptions: { label: string; value: string }[] = [
    { label: 'Pending (belum match)', value: '1' },
    { label: 'Success (sudah match)', value: '0' },
  ];

  userAuth$!: Observable<UserData | null>;
  isAdmin$!: Observable<boolean>;

  constructor(
    private cdRef: ChangeDetectorRef,
    private reconsService: ReconsService,
    private readonly providerService: ProvidersService, // private readonly roleService: RolesService,
    public storeData: Store<any>,
  ) {}

  ngOnInit(): void {
    this.userAuth$ = this.storeData.select(selectCurrentUser);
    this.isAdmin$ = this.userAuth$.pipe(
      map((user) => user?.roles?.some((role) => role.code === 'admin') ?? false)
    );

    const yesterday = this.getYesterday();

    const { startUtc, endUtc } = this.getUtcDateRangeForLocalDate(yesterday);
    this.params.from_date = startUtc;
    this.params.to_date = endUtc;
    this.params.rowPerPage = 10;
    this.params.pageNo = 1;

    this.dateFromConfig = {
      altInput: true,
      altFormat: 'd-m-Y',
      dateFormat: 'Y-m-d',
      defaultDate: yesterday,
    };

    this.userAuth$.subscribe((user) => {
      if (user) {
        this.isAdmin$.pipe(
          takeUntil(this.destroy$)
        ).subscribe((isAdmin) => {
          if (isAdmin) {
            this.getProviderData().then();
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Kick off async "Tarik Data" — calls BE /recons/sync, then polls
   * /recons/sync/:job_id every 3s until status becomes terminal.
   *
   * Re-download semantics: BE soft-deletes existing rows for the
   * (code, date) pair before inserting the fresh batch. The local
   * table re-loads automatically once the worker finishes.
   */
  async triggerSync(): Promise<void> {
    if (!this.params.code) {
      showNotification('error', 'Pilih provider dulu', 'Provider belum dipilih');
      return;
    }
    const day = this.params.from_date instanceof Date ? this.params.from_date : null;
    if (!day) {
      showNotification('error', 'Pilih tanggal dulu', 'Tanggal belum dipilih');
      return;
    }

    // Convert the local-date Date object back to YYYY-MM-DD in Jakarta.
    // params.from_date was built by getUtcDateRangeForLocalDate as the
    // UTC instant for 00:00 Jakarta, so converting it back via the same
    // offset gives the calendar day the user actually picked.
    const dateStr = this.formatJakartaDate(day);

    this.syncing = true;
    try {
      const initial = await firstValueFrom(
        this.reconsService.triggerSync({ code: this.params.code!, date: dateStr })
      );
      const jobId = initial?.data?.job_id;
      if (!jobId) {
        throw new Error('No job_id returned');
      }
      showNotification('success', 'Tarik data dimulai', `Job: ${jobId.substring(0, 8)}...`);
      this.startPolling(jobId);
    } catch (err: any) {
      this.syncing = false;
      const msg = err?.message ?? 'Unknown error';
      showNotification('error', 'Gagal trigger sync', msg);
    }
  }

  private startPolling(jobId: string): void {
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = setInterval(async () => {
      try {
        const resp = await firstValueFrom(this.reconsService.getSyncStatus(jobId));
        const state: ReconSyncStatus | undefined = resp?.data;
        if (!state) return;
        if (state.status === 'completed') {
          this.stopPolling();
          showNotification(
            'success',
            '✅ Sync selesai',
            `${state.rows_inserted} rows imported, ${state.rows_deleted} rows replaced`
          );
          await this.getReconsData();   // refresh table
        } else if (state.status === 'failed') {
          this.stopPolling();
          showNotification('error', '❌ Sync gagal', state.error_message ?? 'Unknown error');
        }
      } catch (err: any) {
        this.stopPolling();
        showNotification('error', 'Polling status gagal', err?.message ?? 'Unknown');
      }
    }, 3000);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.syncing = false;
  }

  /**
   * Click handler for "Match dengan Transaksi".
   * Synchronous BE call — admin gets a count back immediately.
   * Re-clicking wipes prior matches and recomputes.
   */
  async runMatch(): Promise<void> {
    if (!this.params.code) {
      showNotification('error', 'Pilih provider dulu', 'Provider belum dipilih');
      return;
    }
    const day = this.params.from_date instanceof Date ? this.params.from_date : null;
    if (!day) {
      showNotification('error', 'Pilih tanggal dulu', 'Tanggal belum dipilih');
      return;
    }
    const dateStr = this.formatJakartaDate(day);
    this.matching = true;
    try {
      const resp = await firstValueFrom(
        this.reconsService.runMatch({ code: this.params.code!, date: dateStr })
      );
      const r = resp.data;
      const alarm = r.unmatched_success_tx > 0;

      // Title: alert if our successful tx hasn't been reconciled anywhere,
      // success otherwise (even if total matches is 0 — that just means no
      // overlap with our app or apidash, all "external channel" rows).
      const title = alarm
        ? `⚠️ ${r.unmatched_success_tx} transaksi sukses BELUM ke-recon`
        : '✅ Semua transaksi sukses sudah ke-recon';

      // Body: show the two-stage breakdown plus the apidash health
      // signal (fetch errored vs N rows pulled).
      const apidashHint =
        r.apidash_error
          ? ` · ⚠️ apidash error: ${r.apidash_error}`
          : r.apidash_fetched < 0
            ? ''
            : ` · apidash fetched ${r.apidash_fetched}`;
      const body =
        `${r.matched_recon_rows} matched ` +
        `(${r.matched_local} local + ${r.matched_apidash} apidash) · ` +
        `${r.touched_transactions} local tx touched` +
        apidashHint;

      showNotification(alarm ? 'error' : 'success', title, body);
      this.unmatchedTotal = r.unmatched_success_tx;
      await this.getReconsData(); // refresh table to reflect status change
    } catch (err: any) {
      showNotification('error', 'Match gagal', err?.message ?? 'Unknown error');
    } finally {
      this.matching = false;
    }
  }

  /** When user opens the Unmatched tab, fetch the list. */
  async onUnmatchedTabOpen(): Promise<void> {
    this.activeTab = 'unmatched';
    await this.loadUnmatched();
  }

  private async loadUnmatched(): Promise<void> {
    if (!this.params.code) return;
    const day = this.params.from_date instanceof Date ? this.params.from_date : null;
    if (!day) return;
    const dateStr = this.formatJakartaDate(day);
    this.unmatchedLoading = true;
    try {
      const resp = await firstValueFrom(
        this.reconsService.getUnmatched(this.params.code!, dateStr, 1, 100)
      );
      this.unmatchedRows = resp.data?.data ?? [];
      this.unmatchedTotal = resp.data?.total ?? 0;
    } catch (err: any) {
      showNotification('error', 'Load unmatched gagal', err?.message ?? 'Unknown error');
    } finally {
      this.unmatchedLoading = false;
    }
  }

  private formatJakartaDate(d: Date): string {
    // d is a JS Date built from "YYYY-MM-DDT00:00:00+07:00" earlier, so the
    // simplest reliable formatter is to read the original via toLocaleDateString
    // pinned to Asia/Jakarta and reshape to YYYY-MM-DD.
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Jakarta',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return fmt.format(d);
  }

  private async getProviderData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.providerService.getProviders());
      this.providers = data.map((provider) => ({ label: provider.name, value: provider.code }));
      console.log('providerOption: ', this.providers);
    } catch (error) {
      showNotification('error', 'Failed to fetch provider', JSON.stringify(error));
    }
  }

  getYesterday(): string {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const day = String(yesterday.getDate()).padStart(2, '0');
    const month = String(yesterday.getMonth() + 1).padStart(2, '0');
    const year = yesterday.getFullYear();

    return `${year}-${month}-${day}`;
  }

  getUtcDateRangeForLocalDate(localDate: string): { startUtc: Date; endUtc: Date } {
    const timezoneOffset = -new Date().getTimezoneOffset() / 60; // e\.g\. 7 for GMT\+7
    const tz = timezoneOffset >= 0 ? `+${timezoneOffset.toString().padStart(2, '0')}:00` : `-${Math.abs(timezoneOffset).toString().padStart(2, '0')}:00`;

    const localStart =  new Date(`${localDate}T00:00:00${tz}`);
    const localEnd = new Date(`${localDate}T23:59:59${tz}`);

    // const formatDate = (date: Date): string => {
    //   const pad = (n: number) => String(n).padStart(2, '0');
    //   return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
    // };

    return {
      startUtc: localStart,
      endUtc: localEnd,
    };
  }

  formatDateToString(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    // Use UTC values because the date has already been converted to UTC in getUtcDateRangeForLocalDate
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
  }



  async getReconsData(): Promise<void> {
    this.rows = [];
    this.loading = true;
    try {
      const { data, totalPage }: {data: ReconDataResp[], totalPage: number, currentPage: number} = await firstValueFrom(this.reconsService.getProviderRecons(this.params));
      this.rows = this.processDataForTable(data);
      this.totalRows = totalPage;
    } catch (error) {
      showNotification('error', 'Failed to fetch recon', JSON.stringify(error));
    } finally {
      this.loading = false;
    }
  }

  /**
   * Export recons table to Excel in the exact column shape PDAM expects.
   *
   * The PDAM (Perumda Tirta Kerta Raharja) reconciliation file template
   * we're mirroring has 10 columns and a footer row carrying the sum of
   * the `amount` column. Columns (in order):
   *
   *   ID | ref_id | billing_id | merchant | Period | amount |
   *   category | product | created_date | Bank
   *
   * `merchant` and `Bank` are the same value and are inferred from the
   * `ref_id` prefix — see deriveMerchantFromRefId() below.
   *
   * `created_date` is written as an Excel date *serial number* (days
   * since 1899-12-30) so that Excel formats it as a date locally —
   * matches the original PDAM file format.
   *
   * Filename mirrors the PDAM template:
   *   Recon Kilat Teknologi Mandiri  - Perumda Tirta Kerta Raharja - Trx DD Month YYYY.xlsx
   */
  async downloadExcel(): Promise<void> {
    if (!this.params.code) {
      showNotification('error', 'Pilih provider dulu', 'Provider belum dipilih');
      return;
    }
    if (!(this.params.from_date instanceof Date)) {
      showNotification('error', 'Pilih tanggal dulu', 'Tanggal belum dipilih');
      return;
    }

    this.loading = true;
    try {
      // Pull ALL rows for the current filter scope. The recons table is
      // small per-day (≤ 5k rows), so a single big page is fine.
      const params: any = { ...this.params };
      params.rowPerPage = 1000000;
      params.pageNo = 1;
      if (params.from_date instanceof Date) {
        params.fromDate = this.formatDateToString(params.from_date);
        delete params.from_date;
      }
      if (params.to_date instanceof Date) {
        params.toDate = this.formatDateToString(params.to_date);
        delete params.to_date;
      }

      const response = await firstValueFrom(this.reconsService.searchRecons(params));
      const rows = response.data || [];

      // Every row in the PDAM template carries the same date (the file
      // represents one transaction day). Use the user-picked date in
      // Asia/Jakarta to compute the Excel serial.
      const day = this.params.from_date as Date;
      const serial = this.toExcelDateSerial(day);

      // Product label per provider. Defaults to a generic "PDAM" for
      // any code we haven't templated yet.
      const productLabel = this.productLabelFor(this.params.code);
      const code = this.params.code;
      const ymd = this.formatJakartaDate(day).split('-').map(Number); // [yyyy, mm, dd]
      const pad2 = (n: number) => String(n).padStart(2, '0');

      // Branch per-PDAM. The Kab Tangerang template has 10 columns
      // (incl. Bank), the Kota Tangerang template has 9 (no Bank) and
      // hardcodes merchant="KTM" for every row.
      let dataForExcel: any[];
      let headers: string[];
      let colWidths: { wch: number }[];
      let dateCol: number;          // 0-based column index of created_date
      let dateFmt: string;          // Excel format string for created_date
      let fileName: string;
      const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

      if (code === 'PDAMTB001') {
        // ─── PDAM Kota Tangerang ─────────────────────────────────────
        // 9 columns, merchant always "KTM", filename:
        //   Recon PDAM Kota Tangerang YYYYMMDD.xlsx
        headers = [
          'ID', 'ref_id', 'billing_id', 'merchant', 'Period',
          'amount', 'category', 'product', 'created_date',
        ];
        colWidths = [
          { wch: 6 }, { wch: 28 }, { wch: 14 }, { wch: 12 }, { wch: 8 },
          { wch: 14 }, { wch: 10 }, { wch: 32 }, { wch: 14 },
        ];
        dateCol = 8;
        dateFmt = 'm/d/yyyy';
        dataForExcel = rows.map((row, idx) => ({
          'ID': idx + 1,
          'ref_id': row.ref_id,
          'billing_id': row.bill_id,
          'merchant': 'KTM',
          'Period': row.period,
          'amount': Number(row.amount) || 0,
          'category': 'PDAM',
          'product': productLabel,
          'created_date': serial,
        }));
        dataForExcel.push({
          'ID': null, 'ref_id': null, 'billing_id': null, 'merchant': null,
          'Period': null, 'amount': total, 'category': null,
          'product': null, 'created_date': null,
        });
        fileName = `Recon PDAM Kota Tangerang ${ymd[0]}${pad2(ymd[1])}${pad2(ymd[2])}.xlsx`;
      } else {
        // ─── PDAM Kab Tangerang (default) ────────────────────────────
        // 10 columns incl. Bank, merchant derived from bank_code/ref_id,
        // filename mirrors the upstream PDAM template:
        //   Recon Kilat Teknologi Mandiri  - Perumda Tirta Kerta Raharja - Trx DD Bulan YYYY.xlsx
        headers = [
          'ID', 'ref_id', 'billing_id', 'merchant', 'Period',
          'amount', 'category', 'product', 'created_date', 'Bank',
        ];
        colWidths = [
          { wch: 6 }, { wch: 28 }, { wch: 14 }, { wch: 12 }, { wch: 8 },
          { wch: 14 }, { wch: 10 }, { wch: 32 }, { wch: 14 }, { wch: 12 },
        ];
        dateCol = 8;
        dateFmt = 'dd/mm/yyyy';
        dataForExcel = rows.map((row, idx) => {
          const merchant = this.deriveMerchant(row);
          return {
            'ID': idx + 1,
            'ref_id': row.ref_id,
            'billing_id': row.bill_id,
            'merchant': merchant,
            'Period': row.period,
            'amount': Number(row.amount) || 0,
            'category': 'PDAM',
            'product': productLabel,
            'created_date': serial,
            'Bank': merchant,
          };
        });
        dataForExcel.push({
          'ID': null, 'ref_id': null, 'billing_id': null, 'merchant': null,
          'Period': null, 'amount': total, 'category': null,
          'product': null, 'created_date': null, 'Bank': null,
        });
        const monthNamesID = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
        fileName = `Recon Kilat Teknologi Mandiri  - Perumda Tirta Kerta Raharja - Trx ${ymd[2]} ${monthNamesID[ymd[1] - 1]} ${ymd[0]}.xlsx`;
      }

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataForExcel, { header: headers });

      // Format the created_date column as a date instead of a raw number.
      const lastRow = dataForExcel.length + 1; // +1 for header row
      for (let r = 2; r <= lastRow; r++) {
        const dateCell = ws[XLSX.utils.encode_cell({ r: r - 1, c: dateCol })];
        if (dateCell && typeof dateCell.v === 'number') {
          dateCell.t = 'n';
          dateCell.z = dateFmt;
        }
      }

      ws['!cols'] = colWidths;

      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, fileName);
    } catch (error: any) {
      showNotification('error', 'Failed to download excel', error?.message ?? JSON.stringify(error));
    } finally {
      this.loading = false;
    }
  }

  // ─── Bank Mandiri reports (VA + PPOB) ────────────────────────────────

  /**
   * Tooltip text for the Bank Mandiri download buttons when they're
   * disabled. The buttons themselves stay disabled while there's no
   * provider+date selected OR while a previous download is in flight;
   * the actual all_matched gate is checked AT click time (we don't
   * pre-fetch it on every page state change).
   */
  bankMandiriDisabledHint(_kind: 'va' | 'ppob'): string {
    if (!this.params.code) return 'Pilih provider + tanggal dulu';
    if (!(this.params.from_date instanceof Date)) return 'Pilih tanggal dulu';
    return '';
  }

  async downloadBankMandiriVA(): Promise<void> {
    return this.downloadBankMandiri('va');
  }

  async downloadBankMandiriPPOB(): Promise<void> {
    return this.downloadBankMandiri('ppob');
  }

  /**
   * Download one of the two Bank Mandiri Excel reports.
   *
   * Steps:
   *   1. Validate provider + date are selected.
   *   2. Call BE GET /recons/bank-mandiri-report → all_matched + slices.
   *   3. If all_matched=false, abort with a clear notification ("X recons
   *      masih pending — selesaikan match dulu").
   *   4. Build the Excel from the requested slice (va or ppob) using the
   *      template shape carried over from the user's reference files.
   */
  private async downloadBankMandiri(kind: 'va' | 'ppob'): Promise<void> {
    if (!this.params.code) {
      showNotification('error', 'Pilih provider dulu', 'Provider belum dipilih');
      return;
    }
    if (!(this.params.from_date instanceof Date)) {
      showNotification('error', 'Pilih tanggal dulu', 'Tanggal belum dipilih');
      return;
    }
    if (this.params.code !== 'PDAMTKR001') {
      showNotification('error', 'Belum tersedia',
        'Report Bank Mandiri saat ini hanya untuk PDAM Kabupaten Tangerang');
      return;
    }

    this.downloadingBM = true;
    try {
      const day = this.params.from_date as Date;
      const dateStr = this.formatJakartaDate(day); // YYYY-MM-DD Jakarta
      const resp = await firstValueFrom(
        this.reconsService.getBankMandiriReport(this.params.code!, dateStr)
      );
      const data = resp.data;

      if (!data.all_matched) {
        showNotification('error',
          'Reconciliation belum selesai',
          `Masih ada ${data.pending_rows} recon dengan status pending. Match dulu sebelum download.`);
        return;
      }

      const rows: BankMandiriReportRow[] = kind === 'va' ? data.va : data.ppob;
      if (rows.length === 0) {
        showNotification('error',
          'Tidak ada data',
          `Tidak ada transaksi ${kind.toUpperCase()} untuk ${dateStr}.`);
        return;
      }

      this.buildBankMandiriExcel(kind, rows, day);
    } catch (err: any) {
      showNotification('error', 'Gagal download', err?.message ?? JSON.stringify(err));
    } finally {
      this.downloadingBM = false;
    }
  }

  /**
   * Assemble the 10-column Bank Mandiri Excel locally from the row
   * slice the BE returned.
   *
   * Columns:
   *   ID | Ref_id (YYYYMMDD_<billing_id>) | Billing_id | Merchant ("VA" or "PPOB") |
   *   Amount | Fee Bank Mandiri | Total | Category ("PDAM") | Product | Created_date
   *
   * Fee Bank Mandiri rule (per user reference files):
   *   - PPOB → 125 flat
   *   - VA   → 250 for rows 1..9, then 250+(N-9) for row N ≥ 10
   *           (e.g. 250, 250, 250, ..., 250, 251, 252, ...)
   *
   * Total = Amount + Fee.
   *
   * Filename:
   *   VA   → "Recon KTMI-VA-PDAM TKR - Bank Mandiri-DD Bulan YYYY.xlsx"
   *   PPOB → "Recon KTMI-PPOB - PDAM TKR - Bank Mandiri-DD Bulan YYYY.xlsx"
   */
  private buildBankMandiriExcel(kind: 'va' | 'ppob', rows: BankMandiriReportRow[], day: Date): void {
    const merchant = kind === 'va' ? 'VA' : 'PPOB';
    const ymd = this.formatJakartaDate(day).split('-').map(Number); // [yyyy, mm, dd]
    const pad2 = (n: number) => String(n).padStart(2, '0');
    const yyyymmdd = `${ymd[0]}${pad2(ymd[1])}${pad2(ymd[2])}`;
    const serial = this.toExcelDateSerial(day);
    const product = 'Perumdam Kabupaten Tangerang';

    const headers = [
      'ID', 'Ref_id', 'Billing_id', 'Merchant', 'Amount',
      'Fee Bank Mandiri', 'Total', 'Category', 'Product', 'Created_date',
    ];

    const dataForExcel: any[] = rows.map((r, idx) => {
      const fee = this.bankMandiriFee(kind, idx + 1); // 1-based row index
      const amount = Number(r.amount) || 0;
      return {
        'ID': idx + 1,
        'Ref_id': `${yyyymmdd}_${r.billing_id}`,
        'Billing_id': r.billing_id,
        'Merchant': merchant,
        'Amount': amount,
        'Fee Bank Mandiri': fee,
        'Total': amount + fee,
        'Category': 'PDAM',
        'Product': product,
        'Created_date': serial,
      };
    });

    // Footer: sums in Amount / Fee / Total columns. (PPOB reference file
    // leaves Fee+Total blank in footer; user template here mirrors VA's
    // "sum all three" — more useful for reconciliation review.)
    const sumAmount = dataForExcel.reduce((s, r) => s + (r['Amount'] || 0), 0);
    const sumFee = dataForExcel.reduce((s, r) => s + (r['Fee Bank Mandiri'] || 0), 0);
    const sumTotal = dataForExcel.reduce((s, r) => s + (r['Total'] || 0), 0);
    dataForExcel.push({
      'ID': null, 'Ref_id': null, 'Billing_id': null, 'Merchant': null,
      'Amount': sumAmount, 'Fee Bank Mandiri': sumFee, 'Total': sumTotal,
      'Category': null, 'Product': null, 'Created_date': null,
    });

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataForExcel, { header: headers });
    // Format the Created_date column (col J, idx 9) as a date.
    const lastRow = dataForExcel.length + 1; // +1 for header
    for (let r = 2; r <= lastRow; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r: r - 1, c: 9 })];
      if (cell && typeof cell.v === 'number') {
        cell.t = 'n';
        cell.z = 'dd/mm/yyyy';
      }
    }
    ws['!cols'] = [
      { wch: 6 }, { wch: 22 }, { wch: 14 }, { wch: 10 }, { wch: 14 },
      { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 32 }, { wch: 14 },
    ];

    const monthNamesID = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
    const dateLabel = `${ymd[2]} ${monthNamesID[ymd[1] - 1]} ${ymd[0]}`;
    const fileName = kind === 'va'
      ? `Recon KTMI-VA-PDAM TKR - Bank Mandiri-${dateLabel}.xlsx`
      : `Recon KTMI-PPOB - PDAM TKR - Bank Mandiri-${dateLabel}.xlsx`;

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, fileName);
  }

  /**
   * Fee Bank Mandiri rule per user-provided reference files:
   *   - PPOB → 125 flat for every row
   *   - VA   → 250 for rows 1..9, then 250 + (rowNum - 9) starting row 10
   *           (i.e. 250 ×9, 251, 252, 253, ...)
   *
   * `rowNum` is 1-based (first data row = 1).
   */
  private bankMandiriFee(kind: 'va' | 'ppob', rowNum: number): number {
    if (kind === 'ppob') return 125;
    return rowNum <= 9 ? 250 : 250 + (rowNum - 9);
  }

  /**
   * Returns the "merchant" / "Bank" label PDAM uses for a row.
   *
   * Strategy:
   *   1. If `bank_code` is present on the row (rows pulled since
   *      switching_common v0.1.39 / BE commit 7699079), look it up in
   *      the authoritative mapping table below. This is what PDAM
   *      itself uses to label rows in their reconciliation file.
   *   2. Otherwise fall back to the legacy ref_id-prefix heuristic so
   *      older rows still render correctly until they're re-Tariked.
   *
   * Authoritative mapping (cross-verified by Tarik-Data 2026-03-17 vs
   * the user's reference Excel for the same date — 657 rows, zero
   * mismatches):
   *
   *   bank_code      | label
   *   ---------------+-----------
   *   "40004"        | Shopee       (Kiosbank channel — 557/657 = 85 %)
   *   "40003"        | Mitracom     (MCOMPPOB channel)
   *   "CENA"         | VA BCA       (BCA SWIFT prefix)
   *   "BCA"          | VA BCA       (alias)
   *   "BRIN"         | VA BRI
   *   "BMRI"         | VA Mandiri
   *   "BNIN"         | VA BNI
   *   "4000*" (other)| Kilat PPOB   (loket-style channel that isn't Mitracom/Shopee)
   *   anything else  | → bank_code value itself (e.g. "149823773") so
   *                  |    PDAM at least sees the raw channel code
   *
   * Per user direction (2026-05-14):
   *   - "untuk prefix 4000 dan ga ada mapping dari merchant name nya,
   *     itu default nya Kilat PPOB"
   *   - "bank code lain ambil dari merchant name atau kalo ga ada
   *     juga set aja bank code nya"
   *
   * So: 4000* fallback → "Kilat PPOB". Any other unmapped bank_code →
   * display the bank_code string itself (we don't have a separate
   * merchant_name field on the recon row yet; can be added later if
   * upstream exposes it).
   */
  private deriveMerchant(row: ReconDataResp): string {
    const bc = (row.bank_code || '').trim();
    if (bc) {
      const lbl = this.BANK_CODE_LABELS[bc];
      if (lbl) return lbl;
      // Loket-style channels (KODE_BANK 4000*) that aren't Mitracom (40003)
      // or Shopee (40004) are our own white-label PPOB.
      if (bc.startsWith('4000')) return 'Kilat PPOB';
      // Any other unmapped bank_code → use the bank_code string itself
      // as the label (so PDAM at least sees the raw channel identifier).
      return bc;
    }
    // Legacy rows pulled before bank_code was persisted.
    return this.deriveMerchantFromRefId(row.ref_id);
  }

  /**
   * Authoritative bank_code → PDAM merchant label.
   * Unmapped codes fall through to "Kilat PPOB" (see deriveMerchant).
   */
  private readonly BANK_CODE_LABELS: { [k: string]: string } = {
    '40004': 'Shopee',
    '40003': 'Mitracom',
    'CENA':  'VA BCA',
    'BCA':   'VA BCA',
    'BRIN':  'VA BRI',
    'BMRI':  'VA Mandiri',
    'BNIN':  'VA BNI',
  };

  /**
   * LEGACY heuristic — used only when bank_code is not present on a
   * recon row (i.e. rows pulled before switching_common v0.1.39).
   * Re-Tarik Data for the relevant date to populate bank_code and stop
   * relying on this fallback.
   *
   * Pattern reverse-engineered from the 13 PDAM Kab Tangerang sample
   * files the user shared (12-24 Mar 2026); cross-checked against the
   * apidash sister-system payload (KIOSBANK01/40004 → Shopee,
   * MCOMPPOB01/40003 → Mitracom).
   *
   *   38561…       → VA BCA
   *   14721…       → VA BRI
   *   81598… or
   *   71003…       → VA Mandiri
   *   0_2026… or
   *   0_2025…      → Mitracom
   *   0_… or 2_…   → Shopee
   *   anything else → "Kilat PPOB" (default per user policy)
   */
  private deriveMerchantFromRefId(refId: string | undefined): string {
    if (!refId) return 'Kilat PPOB';
    if (refId.startsWith('38561')) return 'VA BCA';
    if (refId.startsWith('14721')) return 'VA BRI';
    if (refId.startsWith('81598') || refId.startsWith('71003')) return 'VA Mandiri';
    if (refId.startsWith('0_2026') || refId.startsWith('0_2025')) return 'Mitracom';
    if (refId.startsWith('0_') || refId.startsWith('2_')) return 'Shopee';
    return 'Kilat PPOB';
  }

  /**
   * Returns the PDAM product label for a given recon provider code.
   * Today we only ship the Kab Tangerang label because that's the format
   * the user has signed off on. Other codes fall back to the recon row's
   * pdam_name where present, or a generic label.
   */
  private productLabelFor(code: string | null | undefined): string {
    if (code === 'PDAMTKR001') return 'Perumdam Kabupaten Tangerang';
    if (code === 'PDAMTB001') return 'PDAM KOTA TANGERANG';
    return 'PDAM'; // safe default for codes we haven't templated yet
  }

  /**
   * Converts a JS Date to an Excel "serial date" — the integer count of
   * days since 1899-12-30 (Excel's epoch with the historic leap-year
   * bug). This is the format the PDAM template stores in column I, so
   * Excel renders it locally as e.g. "20/03/2026".
   *
   * The date is interpreted in Asia/Jakarta to match the upstream PDAM
   * file (transactions are tagged per-Jakarta-day).
   */
  private toExcelDateSerial(d: Date): number {
    const ymd = this.formatJakartaDate(d).split('-').map(Number); // [y, m, day]
    const utcMid = Date.UTC(ymd[0], ymd[1] - 1, ymd[2]);
    const excelEpoch = Date.UTC(1899, 11, 30);
    return Math.round((utcMid - excelEpoch) / 86_400_000);
  }

  async onProviderChange(event: { value: string, label: string }): Promise<void>{
    this.rows = [];
    if (event) {
      this.params.code = event.value;
      await this.getReconsData();
    } else {
      this.params.code = null;
      alert('Please select provider');
    }
  }

  /**
   * Status dropdown change handler.
   *
   * When the user picks (or clears) a value, reset pageNo and refetch.
   * `params.status` is bound to the dropdown via [(ngModel)] and is
   * already serialized by toQueryString() in ReconsService.searchRecons,
   * so the BE receives ?status=0 / ?status=1 / (omitted) as appropriate.
   */
  async onStatusChange(): Promise<void> {
    this.params.pageNo = 1;
    await this.getReconsData();
  }

  async changeServer(data: any): Promise<void> {
    this.params.pageNo = data.current_page;
    this.params.rowPerPage = data.pagesize;
    this.params.filter = data.search;
    await this.getReconsData();
  }


  async onCreatedFromChange(event: any): Promise<void> {
    if (!this.params.code) {
      return ;
    }
    // this.params.from_date = null;

    const selectedCreatedFromDate = event?.target ? event.target.value : event;
    console.log('Selected Created From date:', selectedCreatedFromDate);

    if (!selectedCreatedFromDate) return;

    const selectedDate = new Date(selectedCreatedFromDate);
    const formattedDate = selectedDate.toISOString().split('T')[0];
    console.log('Formatted Created From Date:', formattedDate);

    const { startUtc, endUtc } = this.getUtcDateRangeForLocalDate(selectedCreatedFromDate);
    this.params.from_date = startUtc;
    this.params.to_date = endUtc;
    this.rowsOrigin = [];
    await this.getReconsData();
  }

  processDataForTable(data: ReconDataResp[]) {
    return data.map((each, i) => {
      return {
        ...each,
        no: getOffset(this.params.pageNo, this.params.rowPerPage) + i + 1,
        // trx_date: formattedDate,
      };
    });
  }

  onInputChanged(value: string): void {
    console.log('Debounced Input:', value);
    this.params.filter = '';
    if (value) {
      this.params.filter = value;
    }
    this.cdRef.detectChanges();
  }

}
